'use client';

import { useEffect, useRef } from 'react';
import { useAuth } from '@/lib/auth/auth-context';
import { usePathname, useRouter } from 'next/navigation';
import { usePageTracking } from '@/hooks/usePageTracking';
import { useSubscription } from '@/lib/hooks/useSubscription';

import NavMenu from '@/components/NavMenu';
import { Toaster } from 'sonner';
import CustomLinkChecker from '@/components/CustomLinkChecker';

// Routes that require an active subscription
const SUBSCRIPTION_REQUIRED_ROUTES = [
  '/orders',
  '/calendar',
  '/products',
  '/vendors',
  '/dashboard',
  '/payments',
  '/settings/team',
];

export default function RootLayoutClient({
  children,
}: {
  children: React.ReactNode;
}) {
  const { user, isLoading } = useAuth();
  const pathname = usePathname();
  const router = useRouter();
  const { isSubscribed, isLoading: isSubscriptionLoading } = useSubscription();
  const hasRedirected = useRef(false);

  // Track page views
  usePageTracking();

  // Client-side subscription guard for subscription-required routes
  useEffect(() => {
    if (isLoading || isSubscriptionLoading || !user) return;

    const isSubscriptionRoute = SUBSCRIPTION_REQUIRED_ROUTES.some(route =>
      pathname === route || pathname?.startsWith(`${route}/`)
    );

    if (isSubscriptionRoute && !isSubscribed && !hasRedirected.current) {
      hasRedirected.current = true;
      router.push('/subscription-payment');
    } else if (!isSubscriptionRoute || isSubscribed) {
      hasRedirected.current = false;
    }
  }, [pathname, isSubscribed, isLoading, isSubscriptionLoading, user, router]);

  // Check if current path is the subscription payment page
  const isSubscriptionPaymentPage = pathname === '/subscription-payment' || pathname?.startsWith('/subscription-payment?');

  // Check if current path is the create order page
  const isCreateOrderPage = pathname === '/orders/new' || pathname?.includes('/orders/') && pathname?.includes('/edit');

  // Simplified authentication check - user exists and has a role
  const shouldShowNavMenu = user && user.role && !isLoading && !isSubscriptionPaymentPage;

  return (
    <div className="min-h-screen flex flex-col bg-muted">
      <div className="flex-1">
        {shouldShowNavMenu && user.role && (
          <div className={isCreateOrderPage ? 'hidden md:block' : ''}>
            <NavMenu />
          </div>
        )}
        <main className={`flex-1 transition-all duration-200 ${shouldShowNavMenu ? 'md:pl-16' : ''}`}>
          {children}
        </main>
      </div>
      <Toaster richColors position="top-right" />
      
      {/* Custom Link Checker - shows dialog if user doesn't have custom link */}
      <CustomLinkChecker 
        excludePaths={['/onboarding', '/welcome', '/profile', '/settings', '/subscription-payment']}
        redirectPath="/orders"
      />
    </div>
  );
}
